// src/pages/Moderation/UsersViolationsPage/ui/UserViolationsDrawer/UserViolationsDrawer.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './UserViolationsDrawer.scss';

import type { GetFlagsResponse, ReportInfo } from 'redux/reducers/moderation/types';
import { ELinksName } from 'services/constants/list-links-sidebar';
import { ETabsUsersDetails } from 'services/constants/lists-table';
import { decodeRestrictionValue } from 'untils/decodeRestrictions';
import { fetchWithAuthentication } from 'untils/getFile';
import { getProtectedImageBlob } from 'untils/imageCache';
import { onHttp } from 'untils/onHttp';
import { safeJsonParse } from 'untils/safeJsonParse';

import { Loader } from 'components/Loader';
import { ModerationUser } from '../types';

type UiMedia = {
  kind: 'photo' | 'video' | 'gif' | 'document' | 'audio' | 'unknown';
  url?: string;
  thumbUrl?: string;
  fileName?: string;
};

type UiContent = {
  text?: string;
  url?: string;
  media: UiMedia[];
};

type Props = {
  user: ModerationUser;
  flags: GetFlagsResponse | null;

  // ✅ додай це: репорт, з якого тягнемо message_data/media
  reportInfo?: ReportInfo | null;
  reportLoading?: boolean;
};

const buildMediaUrl = (type: 'photo' | 'document', accessHash?: string) => {
  if (!accessHash) return undefined;
  return `${onHttp()}/storage/${type}/${accessHash}`;
};

const extractReportContent = (reportInfo: ReportInfo): UiContent => {
  const textFromMessage = reportInfo?.message?.message ?? '';
  const messageDataRaw = reportInfo?.message?.message_data ?? '';

  const uiContent: UiContent = {
    text: textFromMessage || undefined,
    url: undefined,
    media: [],
  };

  if (!messageDataRaw) return uiContent;

  let parsed: any = null;
  try {
    parsed = safeJsonParse(messageDataRaw);
  } catch {
    return uiContent;
  }

  const urlCandidate = parsed?.link ?? parsed?.url ?? parsed?.webpage?.url ?? undefined;
  if (urlCandidate) uiContent.url = urlCandidate;

  const mediaBlock = parsed?.media;

  // PHOTO
  const photo = mediaBlock?.photo_FLAGPHOTO;
  if (photo?.access_hash) {
    const photoUrl = buildMediaUrl('photo', photo.access_hash);
    uiContent.media.push({
      kind: 'photo',
      url: photoUrl,
      thumbUrl: photoUrl,
      fileName: 'photo',
    });
    // якщо хочеш — можна ставити url на фото
    // uiContent.url = photoUrl;
  }

  // DOCUMENT / VIDEO / GIF / IMAGE
  const document = mediaBlock?.document;
  if (document?.access_hash) {
    const mime: string | undefined = document?.mime_type;

    const isVideo = mime?.startsWith('video/');
    const isGif = mime === 'image/gif';
    const isImage = mime?.startsWith('image/');

    const documentUrl = buildMediaUrl('document', document.access_hash);

    const kind: UiMedia['kind'] = isVideo
      ? 'video'
      : isGif
      ? 'gif'
      : isImage
      ? 'photo'
      : 'document';

    uiContent.media.push({
      kind,
      url: documentUrl,
      thumbUrl: documentUrl,
      fileName: document?.attributes?.find((a: any) => a?.file_name)?.file_name ?? 'document',
    });
  }

  return uiContent;
};

export const UserViolationsDrawer: React.FC<Props> = ({
  user,
  flags,
  reportInfo,
  reportLoading,
}) => {
  const [mediaSrc, setMediaSrc] = useState<string>();
  const [mediaLoading, setMediaLoading] = useState(false);

  const selectedContent = useMemo(() => {
    if (!reportInfo) return null;
    return extractReportContent(reportInfo);
  }, [reportInfo]);

  const firstMedia = selectedContent?.media?.[0];

  useEffect(() => {
    if (!firstMedia?.url) {
      setMediaSrc(undefined);
      return;
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('token') || '' : '';

    let alive = true;
    let objectUrl: string | undefined;

    getProtectedImageBlob(firstMedia.url, token, fetchWithAuthentication, setMediaLoading)
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setMediaSrc(objectUrl);
      })
      .catch(() => {
        if (alive) setMediaSrc(undefined);
      });

    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [firstMedia?.url]);

  return (
    <aside className="uv-drawer">
      <div className="uv-drawer__title">User details</div>

      <div className="uv-drawer__row">
        <span className="uv-drawer__k">Name</span>
        <span className="uv-drawer__v">{user.name || '—'}</span>
      </div>

      <div className="uv-drawer__row">
        <span className="uv-drawer__k">Username</span>
        <span className="uv-drawer__v">{user.username ? `@${user.username}` : '—'}</span>
      </div>

      <div className="uv-drawer__row">
        <span className="uv-drawer__k">Email</span>
        <span className="uv-drawer__v">{user.email || '—'}</span>
      </div>

      <div className="uv-drawer__row">
        <span className="uv-drawer__k">ID</span>
        <span className="uv-drawer__v">{user.id}</span>
      </div>

      <div className="uv-drawer__row">
        <span className="uv-drawer__k">Created</span>
        <span className="uv-drawer__v">{user.created_at || '—'}</span>
      </div>

      <div className="uv-drawer__row">
        <span className="uv-drawer__k">Last login</span>
        <span className="uv-drawer__v">{user.last_login || '—'}</span>
      </div>

      <div className="uv-drawer__row" style={{ alignItems: 'flex-start' }}>
        <span className="uv-drawer__k">Restrictions</span>
        <span className="uv-drawer__v" style={{ textAlign: 'right' }}>
          {(user.restrictions ?? []).length ? (
            <span>
              {(user.restrictions ?? []).map((r) => (
                <span key={r.restriction} style={{ display: 'block' }}>
                  {flags ? decodeRestrictionValue(r.restriction, flags.data) : null}
                </span>
              ))}
            </span>
          ) : (
            '—'
          )}
        </span>
      </div>

      {/* ✅ Media preview */}
      <div className="uv-drawer__row" style={{ alignItems: 'flex-start' }}>
        <span className="uv-drawer__k">Media</span>
        <span className="uv-drawer__v" style={{ textAlign: 'right' }}>
          {!firstMedia?.url ? (
            '—'
          ) : (
            <div className="uv-drawer__media">
              {reportLoading ? (
                <Loader />
              ) : !firstMedia?.url ? (
                '—'
              ) : mediaLoading ? (
                <Loader />
              ) : firstMedia.kind === 'video' ? (
                <video
                  className="uv-drawer__img"
                  src={mediaSrc}
                  controls
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  className="uv-drawer__img"
                  src={mediaSrc}
                  alt={firstMedia.fileName}
                  loading="lazy"
                />
              )}
            </div>
          )}
        </span>
      </div>

      <div style={{ marginTop: 12 }}>
        <Link
          className="uv-table__open"
          to={`${ELinksName.USER_DETAILS}/${user.id}/${'registered'}/${String(
            ETabsUsersDetails.USER_DATA,
          )}`}
        >
          Go to user page
        </Link>
      </div>
    </aside>
  );
};
